import {BrowserRouter, Location, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import React, {FC, useEffect, useState} from "react";
import {JustChildren} from "../types";
import {useAuth0, withAuthenticationRequired} from "@auth0/auth0-react";
import {KErrorPage} from "@kopjra/uikit";
import {Translate} from "react-redux-i18n";
import type {To} from "@remix-run/router";
import type {NavigateOptions} from "react-router/dist/lib/context";

export let navigate: (to: To, options?: NavigateOptions & {clearSearch?: boolean}) => void;
export let currentLocation: Location;

export interface ProtectedRouteProps {
    element: React.ComponentType;
    requiredClaims?: string[];
}

export const ProtectedRoute: FC<ProtectedRouteProps> = ({ element, requiredClaims }) => {
    const {isAuthenticated, getIdTokenClaims} = useAuth0();
    const [claims, setClaims] = useState<string[] >([]);

    useEffect(() => {
        if (isAuthenticated && requiredClaims) {
            getIdTokenClaims().then((c) => {
                setClaims((c && c["assigned_roles"]) || []);
            });
        }
    }, [getIdTokenClaims, isAuthenticated, requiredClaims]);

    if (!isAuthenticated || !requiredClaims || requiredClaims.some((c) => claims.includes(c))) {
        const WithAuthentication = withAuthenticationRequired(element);
        return <WithAuthentication/>;
    } else {
        return <KErrorPage title={<Translate value="error.page"/>} code={"403"} message={<Translate value="error.forbidden"/>}/>;
    }
}

export const NavigationExtractor: FC<JustChildren> = ({ children }: JustChildren) => {
    const routerNavigate = useNavigate();
    currentLocation = useLocation();
    navigate = ((to: To, options?: NavigateOptions & {clearSearch?: boolean}) => {
        if (!options?.clearSearch) {
            const {search} = currentLocation;
            if (search && search.length > 0) {
                const toSearch = (typeof to === "string") ? (new URL(to, window.location.origin)).search : to.search;
                if (toSearch && toSearch.length > 0) {
                    to = typeof to === "string" ? `${to}&${search.substring(1)}` : { ...to, search: `${toSearch}&${search.substring(1)}`};
                } else{
                    to = typeof to === "string" ? `${to}${search}` : {...to, search};
                }
            }
        }
        routerNavigate(to, options);
    });
    return <>{children}</>;
}

export function useRemoveSearchParams() {
    const [, setParams] = useSearchParams();
    useEffect(() => {
        setParams(new URLSearchParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

export const HookedBrowserRouter: FC<JustChildren> = ({ children }: JustChildren) => (
    <BrowserRouter>
        <NavigationExtractor>{children}</NavigationExtractor>
    </BrowserRouter>
);

/**
 * This hook can be used in any component inside the HookedBrowserRouter
 * to get the query parameters' Map
 */
export function useQuery(): URLSearchParams {
    return new URLSearchParams(useLocation().search);
}

export const Routes = {
    ALL: "*",
    HOME: "/",
    USER_ACCOUNT: "/user/account",
    CART: "/cart",
    PAYMENT: "/payment",
    USER_BILLING: "/billing",
    USER_PAYMENT: "/userpayment",
    USER_SUBSCRIPTIONS: "/usersubscriptions",
    EMAIL_VERIFIED: "/email_verified",
    USER_INVOICES: "/userinvoices",
    SUPER_USE: "/superuse",
    SUPER_ADMIN: "/superadmin",

    SOLUTION_DETAIL(solution?: string, id?: string): string {
        return solution ? `/${solution}${id ? `?cartId=${id}` : ""}` : "/:solution";
    },

    AREA(area?: string): string {
        return area ? `/area/${area}` : "/area/:area";
    }
}
